import { useEffect, useRef, useCallback } from 'react';

const RUNE_CHARS = ['\u2666', '\u2726', '\u2605', '\u2736', '\u2742', '\u2756', '\u2741', '\u273B', '\u2720'];
const FRAME_MS = 1000 / 60; // 16.667ms — reference frame at 60fps

export default function AugmentEffect({ playing, isSkill }) {
  const canvasRef = useRef(null);
  const animationRef = useRef(0);
  const particlesRef = useRef([]);
  const runesRef = useRef([]);
  const activeRef = useRef(false);

  const createParticle = useCallback((cx, cy, type, s, hueBase, delay = 0) => {
    const angle = Math.random() * Math.PI * 2;
    const baseHue = hueBase + (Math.random() - 0.5) * 30;
    const base = {
      x: cx, y: cy, vx: 0, vy: 0, life: 0,
      maxLife: 100, size: 2 * s, angle, orbitRadius: 0,
      orbitSpeed: 0, type, alpha: 0, hue: baseHue, delay,
    };
    switch (type) {
      case 'orbit':
        return { ...base, maxLife: 100 + Math.random() * 60, size: (1.5 + Math.random() * 2.5) * s, orbitRadius: (40 + Math.random() * 90) * s, orbitSpeed: (0.025 + Math.random() * 0.035) * (Math.random() > 0.5 ? 1 : -1) };
      case 'burst':
        return { ...base, vx: Math.cos(angle) * (4 + Math.random() * 8) * s, vy: Math.sin(angle) * (4 + Math.random() * 8) * s, maxLife: 30 + Math.random() * 25, size: (2 + Math.random() * 4) * s, alpha: 1 };
      case 'rising':
        return { ...base, x: cx + (Math.random() - 0.5) * 80 * s, y: cy + (20 + Math.random() * 30) * s, vy: (-1.5 - Math.random() * 2.5) * s, vx: (Math.random() - 0.5) * 0.4 * s, maxLife: 60 + Math.random() * 50, size: (1 + Math.random() * 2) * s };
      case 'glow':
        return { ...base, maxLife: 180, size: (100 + Math.random() * 40) * s };
      case 'spark':
        return { ...base, x: cx + (Math.random() - 0.5) * 140 * s, y: cy + (Math.random() - 0.5) * 140 * s, vx: (Math.random() - 0.5) * 3 * s, vy: (Math.random() - 0.5) * 3 * s, maxLife: 15 + Math.random() * 15, size: (0.8 + Math.random() * 1.5) * s, hue: hueBase + Math.random() * 40 };
      case 'column':
        return { ...base, x: cx + (Math.random() - 0.5) * 30 * s, y: cy + 60 * s, vy: (-3 - Math.random() * 4) * s, vx: (Math.random() - 0.5) * 0.3 * s, maxLife: 50 + Math.random() * 40, size: (1.5 + Math.random() * 2) * s, hue: hueBase - 5 + Math.random() * 20 };
      case 'ring':
        return { ...base, maxLife: 50 + Math.random() * 20, size: 5 * s, orbitRadius: 0 };
      default:
        return base;
    }
  }, []);

  const createRune = useCallback((cx, cy, s) => {
    const angle = Math.random() * Math.PI * 2;
    const dist = (50 + Math.random() * 70) * s;
    return {
      x: cx + Math.cos(angle) * dist,
      y: cy + Math.sin(angle) * dist,
      alpha: 0, rotation: Math.random() * Math.PI * 2,
      scale: (0.6 + Math.random() * 0.8) * s,
      life: 0, maxLife: 70 + Math.random() * 30,
      char: RUNE_CHARS[Math.floor(Math.random() * RUNE_CHARS.length)],
    };
  }, []);

  useEffect(() => {
    if (!playing) {
      activeRef.current = false;
      // Clear canvas when not playing
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      return;
    }
    if (activeRef.current) return;
    activeRef.current = true;

    particlesRef.current = [];
    runesRef.current = [];

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: false });
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const parent = canvas.parentElement;
    const w = parent.clientWidth;
    const h = parent.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    ctx.scale(dpr, dpr);

    const cx = w / 2;
    const cy = 50; // center of item/stone boxes (100px tall, top-aligned)
    const s = Math.min(w, h) / 600;

    const particles = particlesRef.current;
    const runes = runesRef.current;

    // Color: purple for skill, yellow for no-skill
    const hue = isSkill ? 270 : 45;

    // Phase thresholds in ms (same real-time duration at any fps)
    const P_INTENSE = (isSkill ? 48 : 32) * FRAME_MS;
    const P_GLOW = (isSkill ? 117 : 64) * FRAME_MS;
    const P_FADE = (isSkill ? 213 : 104) * FRAME_MS;
    const P_END = (isSkill ? 288 : 144) * FRAME_MS;

    let elapsed = 0;
    let lastTime = 0;
    let firstFrame = true;

    // Spawn accumulators keyed by spawn type — returns how many to spawn
    const spawnTimers = {};
    const getSpawns = (key, intervalFrames, dtMs) => {
      if (!spawnTimers[key]) spawnTimers[key] = 0;
      spawnTimers[key] += dtMs;
      const intervalMs = intervalFrames * FRAME_MS;
      let count = 0;
      while (spawnTimers[key] >= intervalMs && count < 3) {
        spawnTimers[key] -= intervalMs;
        count++;
      }
      return count;
    };

    const animate = (now) => {
      if (!activeRef.current) return;

      // Delta time calculation
      if (lastTime === 0) lastTime = now;
      const rawDt = now - lastTime;
      lastTime = now;
      const cappedDt = Math.min(rawDt, 50); // Cap to prevent jumps on tab-switch
      elapsed += cappedDt;
      const dt = cappedDt / FRAME_MS; // Delta in reference-frame units (1.0 at 60fps)
      // Virtual frame count for oscillation/rotation expressions
      const tFrames = elapsed / FRAME_MS;

      let phase;
      if (elapsed < P_INTENSE) phase = 0;
      else if (elapsed < P_GLOW) phase = 1;
      else if (elapsed < P_FADE) phase = 2;
      else if (elapsed < P_END) phase = 3;
      else {
        ctx.clearRect(0, 0, w, h);
        activeRef.current = false;
        return;
      }

      ctx.clearRect(0, 0, w, h);

      // === AMBIENT (keep gradient - only 1 per frame, centered) ===
      const ambAlpha = phase === 0 ? 0.25 : phase === 1 ? 0.18 : phase === 2 ? 0.1 : 0.04;
      const ambGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, w * 0.45);
      ambGrad.addColorStop(0, `hsla(${hue}, 60%, 25%, ${ambAlpha})`);
      ambGrad.addColorStop(0.5, `hsla(${hue + 5}, 50%, 12%, ${ambAlpha * 0.5})`);
      ambGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = ambGrad;
      ctx.fillRect(0, 0, w, h);

      // === SPAWN ===
      if (phase === 0) {
        if (firstFrame) {
          for (let i = 0; i < 60; i++) particles.push(createParticle(cx, cy, 'burst', s, hue));
          for (let i = 0; i < 35; i++) particles.push(createParticle(cx, cy, 'spark', s, hue));
          particles.push(createParticle(cx, cy, 'glow', s, hue));
          for (let i = 0; i < 28; i++) {
            const p = createParticle(cx, cy, 'ring', s, hue);
            p.angle = (i / 28) * Math.PI * 2;
            particles.push(p);
          }
          for (let i = 0; i < 6; i++) runes.push(createRune(cx, cy, s));
          firstFrame = false;
        }
        for (let n = 0; n < getSpawns('rising_p0', 3, cappedDt); n++)
          particles.push(createParticle(cx, cy, 'rising', s, hue));
        for (let n = 0; n < getSpawns('column_p0', 3, cappedDt); n++)
          particles.push(createParticle(cx, cy, 'column', s, hue));
      } else if (phase === 1) {
        for (let n = 0; n < getSpawns('orbit_p1', 2, cappedDt); n++) {
          particles.push(createParticle(cx, cy, 'orbit', s, hue));
          particles.push(createParticle(cx, cy, 'rising', s, hue));
        }
        for (let n = 0; n < getSpawns('column_p1', 3, cappedDt); n++)
          particles.push(createParticle(cx, cy, 'column', s, hue));
        for (let n = 0; n < getSpawns('spark_p1', 4, cappedDt); n++)
          particles.push(createParticle(cx, cy, 'spark', s, hue));
        for (let n = 0; n < getSpawns('rune_p1', 6, cappedDt); n++)
          runes.push(createRune(cx, cy, s));
      } else if (phase === 2) {
        for (let n = 0; n < getSpawns('rising_p2', 5, cappedDt); n++)
          particles.push(createParticle(cx, cy, 'rising', s, hue));
        for (let n = 0; n < getSpawns('column_p2', 8, cappedDt); n++)
          particles.push(createParticle(cx, cy, 'column', s, hue));
      } else {
        for (let n = 0; n < getSpawns('rising_p3', 12, cappedDt); n++)
          particles.push(createParticle(cx, cy, 'rising', s, hue));
      }

      // === CENTRAL GLOW (keep gradient - only 1 per frame, centered) ===
      const gi =
        phase === 0 ? 0.7 + Math.sin(tFrames * 0.3) * 0.3
        : phase === 1 ? Math.max(0.1, 0.5 - ((elapsed - P_INTENSE) / (P_GLOW - P_INTENSE)) * 0.4)
        : phase === 2 ? Math.max(0, 0.15 * (1 - (elapsed - P_GLOW) / (P_FADE - P_GLOW)))
        : 0;
      if (gi > 0.01) {
        const cg = ctx.createRadialGradient(cx, cy, 0, cx, cy, 140 * s);
        cg.addColorStop(0, `hsla(${hue + 5}, 100%, 85%, ${gi * 0.6})`);
        cg.addColorStop(0.15, `hsla(${hue}, 90%, 65%, ${gi * 0.4})`);
        cg.addColorStop(0.4, `hsla(${hue + 8}, 80%, 45%, ${gi * 0.2})`);
        cg.addColorStop(0.7, `hsla(${hue + 10}, 70%, 25%, ${gi * 0.08})`);
        cg.addColorStop(1, 'transparent');
        ctx.fillStyle = cg;
        ctx.fillRect(0, 0, w, h);
      }

      // === MAGIC CIRCLE (phases 1-2) — no shadowBlur ===
      if (phase >= 1 && phase <= 2) {
        const cp = phase === 1
          ? (elapsed - P_INTENSE) / (P_GLOW - P_INTENSE)
          : Math.max(0, 1 - (elapsed - P_GLOW) / (P_FADE - P_GLOW));
        const ca = cp * 0.6;

        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(tFrames * 0.008);

        // Outer circle — draw twice (thicker + thinner) to fake glow
        ctx.beginPath();
        ctx.arc(0, 0, 110 * s, 0, Math.PI * 2);
        ctx.strokeStyle = `hsla(${hue + 5}, 80%, 65%, ${ca * 0.3})`;
        ctx.lineWidth = 6 * s;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(0, 0, 110 * s, 0, Math.PI * 2);
        ctx.strokeStyle = `hsla(${hue + 5}, 80%, 65%, ${ca * 0.7})`;
        ctx.lineWidth = 2.5 * s;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(0, 0, 80 * s, 0, Math.PI * 2);
        ctx.strokeStyle = `hsla(${hue - 2}, 75%, 60%, ${ca * 0.5})`;
        ctx.lineWidth = 1.5 * s;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(0, 0, 50 * s, 0, Math.PI * 2);
        ctx.strokeStyle = `hsla(${hue + 10}, 70%, 70%, ${ca * 0.4})`;
        ctx.lineWidth = 1 * s;
        ctx.stroke();

        for (let i = 0; i < 6; i++) {
          const a1 = (i / 6) * Math.PI * 2;
          const a2 = ((i + 2) / 6) * Math.PI * 2;
          ctx.beginPath();
          ctx.moveTo(Math.cos(a1) * 110 * s, Math.sin(a1) * 110 * s);
          ctx.lineTo(Math.cos(a2) * 110 * s, Math.sin(a2) * 110 * s);
          ctx.strokeStyle = `hsla(${hue + 5}, 60%, 55%, ${ca * 0.3})`;
          ctx.lineWidth = 1 * s;
          ctx.stroke();
        }

        for (let i = 0; i < 8; i++) {
          const a = (i / 8) * Math.PI * 2;
          ctx.beginPath();
          ctx.moveTo(Math.cos(a) * 50 * s, Math.sin(a) * 50 * s);
          ctx.lineTo(Math.cos(a) * 110 * s, Math.sin(a) * 110 * s);
          ctx.strokeStyle = `hsla(${hue + 10}, 65%, 55%, ${ca * 0.25})`;
          ctx.lineWidth = 0.8 * s;
          ctx.stroke();
        }

        // Diamond nodes — no shadowBlur, use two fills for glow
        for (let i = 0; i < 8; i++) {
          const a = (i / 8) * Math.PI * 2;
          const nx = Math.cos(a) * 110 * s;
          const ny = Math.sin(a) * 110 * s;
          ctx.save();
          ctx.translate(nx, ny);
          ctx.rotate(a + tFrames * 0.02);
          // Outer glow diamond (larger, dimmer)
          ctx.beginPath();
          ctx.moveTo(0, -9 * s);
          ctx.lineTo(7.5 * s, 0);
          ctx.lineTo(0, 9 * s);
          ctx.lineTo(-7.5 * s, 0);
          ctx.closePath();
          ctx.fillStyle = `hsla(${hue + 5}, 100%, 80%, ${ca * 0.3})`;
          ctx.fill();
          // Core diamond
          ctx.beginPath();
          ctx.moveTo(0, -6 * s);
          ctx.lineTo(5 * s, 0);
          ctx.lineTo(0, 6 * s);
          ctx.lineTo(-5 * s, 0);
          ctx.closePath();
          ctx.fillStyle = `hsla(${hue}, 90%, 80%, ${ca * 0.9})`;
          ctx.fill();
          ctx.restore();
        }

        ctx.restore();
      }

      // === DRAW PARTICLES — no gradients, no shadowBlur ===
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        if (p.delay > 0) { p.delay -= dt; continue; }
        p.life += dt;
        if (p.life > p.maxLife) { particles.splice(i, 1); continue; }
        const lr = p.life / p.maxLife;

        switch (p.type) {
          case 'orbit': {
            p.angle += p.orbitSpeed * dt;
            p.orbitRadius *= Math.pow(1 - lr * 0.4, dt);
            p.x = cx + Math.cos(p.angle) * p.orbitRadius;
            p.y = cy + Math.sin(p.angle) * p.orbitRadius * 0.6;
            p.alpha = lr < 0.1 ? lr * 10 : lr > 0.8 ? (1 - lr) * 5 : 1;
            // Outer glow circle (replaces radial gradient)
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * 4, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${p.hue}, 80%, 55%, ${p.alpha * 0.15})`;
            ctx.fill();
            // Mid glow
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * 2, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${p.hue}, 90%, 75%, ${p.alpha * 0.35})`;
            ctx.fill();
            // Bright core
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * 0.8, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${p.hue}, 100%, 92%, ${p.alpha})`;
            ctx.fill();
            break;
          }
          case 'burst': {
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            p.vx *= Math.pow(0.95, dt);
            p.vy *= Math.pow(0.95, dt);
            p.alpha = Math.max(0, 1 - lr * 1.2);
            // Outer glow
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * 3, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${p.hue}, 90%, 65%, ${p.alpha * 0.2})`;
            ctx.fill();
            // Bright core
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * (1 - lr * 0.6), 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${p.hue + 10}, 100%, 96%, ${p.alpha * 0.9})`;
            ctx.fill();
            break;
          }
          case 'rising': {
            p.x += (p.vx + Math.sin(p.life * 0.08) * 0.4 * s) * dt;
            p.y += p.vy * dt;
            p.alpha = (lr < 0.15 ? lr / 0.15 : lr > 0.7 ? (1 - lr) / 0.3 : 1) * 0.75;
            // Outer glow
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * 3, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${p.hue}, 90%, 75%, ${p.alpha * 0.2})`;
            ctx.fill();
            // Core
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * 0.5, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${p.hue}, 100%, 93%, ${p.alpha})`;
            ctx.fill();
            break;
          }
          case 'column': {
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            p.vy *= Math.pow(0.99, dt);
            p.alpha = (lr < 0.1 ? lr * 10 : lr > 0.6 ? (1 - lr) / 0.4 : 1) * 0.6;
            ctx.save();
            ctx.globalAlpha = p.alpha;
            // Outer glow ellipse
            ctx.beginPath();
            ctx.ellipse(p.x, p.y, p.size * 2, p.size * 6, 0, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${p.hue}, 85%, 60%, 0.25)`;
            ctx.fill();
            // Core
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * 0.4, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${p.hue}, 100%, 95%, 1)`;
            ctx.fill();
            ctx.globalAlpha = 1;
            ctx.restore();
            break;
          }
          case 'glow': {
            // Keep gradient for glow — only 1 particle of this type exists
            p.alpha = lr < 0.08 ? lr / 0.08 : lr > 0.4 ? Math.max(0, (1 - lr) * 1.67) : 1;
            const gg = ctx.createRadialGradient(cx, cy, 0, cx, cy, p.size * (0.8 + lr * 0.5));
            gg.addColorStop(0, `hsla(${hue + 5}, 100%, 95%, ${p.alpha * 0.8})`);
            gg.addColorStop(0.15, `hsla(${hue}, 95%, 78%, ${p.alpha * 0.5})`);
            gg.addColorStop(0.4, `hsla(${hue + 8}, 85%, 50%, ${p.alpha * 0.2})`);
            gg.addColorStop(1, 'transparent');
            ctx.fillStyle = gg;
            ctx.fillRect(0, 0, w, h);
            break;
          }
          case 'spark': {
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            p.alpha = lr < 0.2 ? lr * 5 : Math.max(0, 1 - lr * 1.1);
            // Glow circle (replaces shadowBlur)
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * 3, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${p.hue}, 100%, 80%, ${p.alpha * 0.25})`;
            ctx.fill();
            // Bright core
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${p.hue}, 100%, 95%, ${p.alpha})`;
            ctx.fill();
            break;
          }
          case 'ring': {
            p.orbitRadius += (4 + lr * 2) * s * dt;
            p.x = cx + Math.cos(p.angle) * p.orbitRadius;
            p.y = cy + Math.sin(p.angle) * p.orbitRadius;
            p.alpha = Math.max(0, 1 - lr * 1.5);
            // Glow circle (replaces shadowBlur)
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * 2.5 * (1 - lr * 0.7), 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${hue + 5}, 100%, 80%, ${p.alpha * 0.15})`;
            ctx.fill();
            // Core
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * (1 - lr * 0.7), 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${hue + 5}, 90%, 85%, ${p.alpha * 0.6})`;
            ctx.fill();
            break;
          }
          default:
            break;
        }
      }

      // === RUNES — no shadowBlur ===
      for (let i = runes.length - 1; i >= 0; i--) {
        const r = runes[i];
        r.life += dt;
        if (r.life > r.maxLife) { runes.splice(i, 1); continue; }
        const rlr = r.life / r.maxLife;
        r.alpha = (rlr < 0.05 ? rlr / 0.05 : rlr > 0.65 ? (1 - rlr) / 0.35 : 1) * 0.65;
        r.y -= 0.4 * s * dt;
        r.rotation += 0.025 * dt;
        ctx.save();
        ctx.translate(r.x, r.y);
        ctx.rotate(r.rotation);
        ctx.font = `${18 * r.scale}px serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = `hsla(${hue + 5}, 80%, 85%, ${r.alpha})`;
        ctx.fillText(r.char, 0, 0);
        ctx.restore();
      }

      // === SCREEN FLASH (burst phase — first ~167ms) ===
      const flashEnd = 10 * FRAME_MS;
      if (phase === 0 && elapsed < flashEnd) {
        const fa = Math.max(0, 1 - elapsed / flashEnd) * 0.7;
        const fg = ctx.createRadialGradient(cx, cy, 0, cx, cy, w * 0.6);
        fg.addColorStop(0, `hsla(${hue + 5}, 100%, 95%, ${fa})`);
        fg.addColorStop(0.3, `hsla(${hue}, 90%, 70%, ${fa * 0.5})`);
        fg.addColorStop(1, 'transparent');
        ctx.fillStyle = fg;
        ctx.fillRect(0, 0, w, h);
      }

      // === PULSING CORE — no shadowBlur, use concentric circles ===
      if (phase <= 2) {
        const pulse = 0.5 + Math.sin(tFrames * 0.15) * 0.3;
        const coreA = phase === 0 ? 0.9 : phase === 1 ? pulse * 0.5 : pulse * 0.3;
        // Outer glow
        ctx.beginPath();
        ctx.arc(cx, cy, (18 + pulse * 8) * s, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${hue + 5}, 100%, 80%, ${coreA * 0.2})`;
        ctx.fill();
        // Mid glow
        ctx.beginPath();
        ctx.arc(cx, cy, (12 + pulse * 6) * s, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${hue + 5}, 100%, 85%, ${coreA * 0.4})`;
        ctx.fill();
        // Bright core
        ctx.beginPath();
        ctx.arc(cx, cy, (8 + pulse * 4) * s, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${hue + 5}, 100%, 92%, ${coreA})`;
        ctx.fill();
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    // Run first frame synchronously for instant visual feedback
    animate(performance.now());

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      activeRef.current = false;
    };
  }, [playing, isSkill, createParticle, createRune]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1,
        willChange: 'transform',
      }}
    />
  );
}
